import { ShieldCheck } from 'lucide-react';
import COUNTRIES from '../utils/countries';

export const ACKNOWLEDGEMENT_TEXT = 'I confirm that the information provided regarding the purchaser, destination, end user and intended use is accurate and understand that supply is subject to applicable export-control and sanctions requirements.';

export const EMPTY_COMPLIANCE = {
  company_legal_name: '', country_of_incorporation: '', hq_country: '',
  ultimate_parent_name: '', ultimate_parent_hq_country: '',
  delivery_destination: '', installation_country: '',
  buyer_is_end_user: '', end_user_name: '', end_user_country: '',
  intended_use: '', reexport_intended: '', acknowledged: false,
};

const inputCls = 'w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope text-sm';
const labelCls = 'block font-manrope text-sm font-medium text-slate-700 mb-2';

const CountrySelect = (props) => (
  <select name={props.name} value={props.value || ''} onChange={props.onChange} required={props.required} className={inputCls} data-testid={`compliance-${props.name}`}>
    <option value="">Select country</option>
    {COUNTRIES.map((c) => (c.disabled ? <option key={c.code} disabled>─────────</option> : <option key={c.code} value={c.name}>{c.name}</option>))}
  </select>
);

const YesNo = (props) => (
  <select name={props.name} value={props.value || ''} onChange={props.onChange} required className={inputCls} data-testid={`compliance-${props.name}`}>
    <option value="">Select</option>
    <option value="yes">Yes</option>
    <option value="no">No</option>
  </select>
);

export const ComplianceFieldset = ({ values, onChange }) => {
  const set = (e) => onChange(e.target.name, e.target.type === 'checkbox' ? e.target.checked : e.target.value);
  const needsEndUser = values.buyer_is_end_user === 'no';

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 space-y-5" data-testid="compliance-fieldset">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-[#0096D6]" />
        </span>
        <div>
          <p className="font-outfit text-sm font-semibold text-slate-900 uppercase tracking-wide">Export-Control Due Diligence</p>
          <p className="font-manrope text-xs text-slate-500 mt-1 leading-relaxed">
            Advanced computing hardware may be subject to export controls. This information allows our compliance team to review the transaction. Completing it does not constitute approval or refusal of supply.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Company Legal Name *</label>
          <input name="company_legal_name" value={values.company_legal_name} onChange={set} required className={inputCls} data-testid="compliance-company_legal_name" />
        </div>
        <div>
          <label className={labelCls}>Country of Incorporation *</label>
          <CountrySelect name="country_of_incorporation" value={values.country_of_incorporation} onChange={set} required />
        </div>
        <div>
          <label className={labelCls}>Company Headquarters Country *</label>
          <CountrySelect name="hq_country" value={values.hq_country} onChange={set} required />
        </div>
        <div>
          <label className={labelCls}>Ultimate Parent Company</label>
          <input name="ultimate_parent_name" value={values.ultimate_parent_name} onChange={set} placeholder="If applicable" className={inputCls} data-testid="compliance-ultimate_parent_name" />
        </div>
        <div>
          <label className={labelCls}>Ultimate Parent Headquarters Country</label>
          <CountrySelect name="ultimate_parent_hq_country" value={values.ultimate_parent_hq_country} onChange={set} />
        </div>
        <div>
          <label className={labelCls}>Delivery Destination *</label>
          <input name="delivery_destination" value={values.delivery_destination} onChange={set} required placeholder="City, Country" className={inputCls} data-testid="compliance-delivery_destination" />
        </div>
        <div>
          <label className={labelCls}>Final Installation / Deployment Country *</label>
          <CountrySelect name="installation_country" value={values.installation_country} onChange={set} required />
        </div>
        <div>
          <label className={labelCls}>Is the buyer the ultimate end user? *</label>
          <YesNo name="buyer_is_end_user" value={values.buyer_is_end_user} onChange={set} />
        </div>
        {needsEndUser && (
          <>
            <div>
              <label className={labelCls}>Ultimate End-User Name *</label>
              <input name="end_user_name" value={values.end_user_name} onChange={set} required className={inputCls} data-testid="compliance-end_user_name" />
            </div>
            <div>
              <label className={labelCls}>Ultimate End-User Country *</label>
              <CountrySelect name="end_user_country" value={values.end_user_country} onChange={set} required />
            </div>
          </>
        )}
        <div className="md:col-span-2">
          <label className={labelCls}>Intended Use *</label>
          <textarea name="intended_use" value={values.intended_use} onChange={set} required rows={3} placeholder="e.g. AI model training for internal research and development" className={`${inputCls} resize-none`} data-testid="compliance-intended_use" />
        </div>
        <div>
          <label className={labelCls}>Intended re-export or resale? *</label>
          <YesNo name="reexport_intended" value={values.reexport_intended} onChange={set} />
        </div>
      </div>

      <label className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200 cursor-pointer">
        <input type="checkbox" name="acknowledged" checked={!!values.acknowledged} onChange={set} required className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#0096D6]" data-testid="compliance-acknowledged" />
        <span className="font-manrope text-sm text-slate-600 leading-relaxed">{ACKNOWLEDGEMENT_TEXT}</span>
      </label>
    </div>
  );
};
